import { useState, MouseEvent } from "react";
import {
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Collapse,
  List,
} from "@material-ui/core";
import { Link } from "@backstage/core-components";
import { usePermission } from "@backstage/plugin-permission-react";
import {
  stanReadPermission,
  stanTasksPermission,
  stanAdminPermission,
  hrWritePermission,
} from "../../../../permissions";
import AndroidIcon from "@material-ui/icons/Android";
import DashboardIcon from "@material-ui/icons/Dashboard";
import SecurityIcon from "@material-ui/icons/Security";
import NotificationsIcon from "@material-ui/icons/Notifications";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ListAltIcon from "@material-ui/icons/ListAlt";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { useRootStyles } from "../../styles/useRootStyles";

interface StanNavItemProps {
  mobile?: boolean;
  onClick?: () => void;
  displayName?: string;
}

const isPermissionDenied = (p: { loading: boolean; allowed?: boolean }) =>
  p.loading || !p.allowed;

const hasAnyStanPermission = (
  read: { loading: boolean; allowed?: boolean },
  tasks: { loading: boolean; allowed?: boolean },
  admin: { loading: boolean; allowed?: boolean }
) =>
  !isPermissionDenied(read) ||
  !isPermissionDenied(tasks) ||
  !isPermissionDenied(admin);

interface StanMenuItemsProps {
  hasReadAccess: boolean;
  hasTasksAccess: boolean;
  hasAdminAccess: boolean;
  hrWriteAllowed: boolean | undefined;
  closeHandler: () => void;
  paddingLeft?: number;
}

const StanMenuItems = ({
  hasReadAccess,
  hasTasksAccess,
  hasAdminAccess,
  hrWriteAllowed,
  closeHandler,
  paddingLeft = 0,
}: StanMenuItemsProps) => (
  <>
    {hasReadAccess && (
      <>
        <MenuItem
          component={Link}
          to="/stan/dashboard"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          Dashboard
        </MenuItem>
        <MenuItem
          component={Link}
          to="/stan/agents"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <AndroidIcon fontSize="small" />
          </ListItemIcon>
          Agents
        </MenuItem>
        <MenuItem
          component={Link}
          to="/stan/policy-rules"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <SecurityIcon fontSize="small" />
          </ListItemIcon>
          Policy Rules
        </MenuItem>
        <MenuItem
          component={Link}
          to="/stan/notifications"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          Notifications
        </MenuItem>
      </>
    )}
    {hasTasksAccess && (
      <>
        <MenuItem
          component={Link}
          to="/stan/tasks"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          Tasks
        </MenuItem>
        <MenuItem
          component={Link}
          to="/stan/periodic-tasks"
          onClick={closeHandler}
          style={{ paddingLeft }}
        >
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          Periodic Tasks
        </MenuItem>
      </>
    )}
    {hasAdminAccess && (
      <MenuItem
        component={Link}
        to="/stan/logs"
        onClick={closeHandler}
        style={{ paddingLeft }}
      >
        <ListItemIcon>
          <ListAltIcon fontSize="small" />
        </ListItemIcon>
        Audit Logs
      </MenuItem>
    )}
    {hrWriteAllowed && (
      <MenuItem
        component={Link}
        to="/stan/upload"
        onClick={closeHandler}
        style={{ paddingLeft }}
      >
        <ListItemIcon>
          <CloudUploadIcon fontSize="small" />
        </ListItemIcon>
        Upload HR Data
      </MenuItem>
    )}
  </>
);

interface StanMobileListProps {
  hasReadAccess: boolean;
  hasTasksAccess: boolean;
  hasAdminAccess: boolean;
  hrWriteAllowed: boolean | undefined;
  onClick?: () => void;
  classes: ReturnType<typeof useRootStyles>;
}

const StanMobileList = ({
  hasReadAccess,
  hasTasksAccess,
  hasAdminAccess,
  hrWriteAllowed,
  onClick,
  classes,
}: StanMobileListProps) => (
  <>
    {hasReadAccess && (
      <>
        <ListItem
          button
          component={Link}
          to="/stan/dashboard"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/stan/agents"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <AndroidIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Agents" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/stan/policy-rules"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <SecurityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Policy Rules" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/stan/notifications"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Notifications" />
        </ListItem>
      </>
    )}
    {hasTasksAccess && (
      <>
        <ListItem
          button
          component={Link}
          to="/stan/tasks"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Tasks" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/stan/periodic-tasks"
          className={classes.drawerItem}
          onClick={onClick}
          style={{ paddingLeft: 48 }}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Periodic Tasks" />
        </ListItem>
      </>
    )}
    {hasAdminAccess && (
      <ListItem
        button
        component={Link}
        to="/stan/logs"
        className={classes.drawerItem}
        onClick={onClick}
        style={{ paddingLeft: 48 }}
      >
        <ListItemIcon className={classes.drawerIcon}>
          <ListAltIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Audit Logs" />
      </ListItem>
    )}
    {hrWriteAllowed && (
      <ListItem
        button
        component={Link}
        to="/stan/upload"
        className={classes.drawerItem}
        onClick={onClick}
        style={{ paddingLeft: 48 }}
      >
        <ListItemIcon className={classes.drawerIcon}>
          <CloudUploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Upload HR Data" />
      </ListItem>
    )}
  </>
);

export const StanNavItem = ({
  mobile = false,
  onClick,
  displayName = "STAN",
}: StanNavItemProps) => {
  const classes = useRootStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check permissions
  const stanReadPerm = usePermission({ permission: stanReadPermission });
  const stanTasksPerm = usePermission({ permission: stanTasksPermission });
  const stanAdminPerm = usePermission({ permission: stanAdminPermission });
  const hrWritePerm = usePermission({ permission: hrWritePermission });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Don't render if user doesn't have any STAN permissions
  if (!hasAnyStanPermission(stanReadPerm, stanTasksPerm, stanAdminPerm)) {
    return null;
  }

  const hasReadAccess =
    stanReadPerm.allowed || stanTasksPerm.allowed || stanAdminPerm.allowed;
  const hasTasksAccess = stanTasksPerm.allowed || stanAdminPerm.allowed;
  const hasAdminAccess = stanAdminPerm.allowed;

  // Check if used in a dropdown menu (has onClick but not mobile)
  if (onClick && !mobile) {
    return (
      <>
        <MenuItem disabled>
          <ListItemIcon>
            <AndroidIcon fontSize="small" />
          </ListItemIcon>
          {displayName}
        </MenuItem>
        <StanMenuItems
          hasReadAccess={!!hasReadAccess}
          hasTasksAccess={!!hasTasksAccess}
          hasAdminAccess={!!hasAdminAccess}
          hrWriteAllowed={hrWritePerm.allowed}
          closeHandler={onClick}
          paddingLeft={32}
        />
      </>
    );
  }

  if (mobile) {
    return (
      <>
        <ListItem
          button
          onClick={handleMobileToggle}
          className={classes.drawerItem}
        >
          <ListItemIcon className={classes.drawerIcon}>
            <AndroidIcon />
          </ListItemIcon>
          <ListItemText primary={displayName} />
          {mobileOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={mobileOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <StanMobileList
              hasReadAccess={!!hasReadAccess}
              hasTasksAccess={!!hasTasksAccess}
              hasAdminAccess={!!hasAdminAccess}
              hrWriteAllowed={hrWritePerm.allowed}
              onClick={onClick}
              classes={classes}
            />
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <>
      <Button
        className={classes.navButton}
        startIcon={<AndroidIcon />}
        endIcon={<ArrowDropDownIcon />}
        color="inherit"
        onClick={handleClick}
      >
        {displayName}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <StanMenuItems
          hasReadAccess={!!hasReadAccess}
          hasTasksAccess={!!hasTasksAccess}
          hasAdminAccess={!!hasAdminAccess}
          hrWriteAllowed={hrWritePerm.allowed}
          closeHandler={handleClose}
        />
      </Menu>
    </>
  );
};
